import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAuthStore, isPlatformRole, isTenantRole, isMunicipalityRole } from '@store/authStore'
import { Suspense, lazy, useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from '@components/shared/ErrorBoundary'
import { ErrorPage } from '@components/shared/ErrorPage'
import { useGlobalNepaliKeyboard } from '@hooks/useGlobalNepaliKeyboard'
import { landingPath } from '@utils/landing'
import { getPortalContext, getMainLoginUrl, getTenantLoginUrl } from '@utils/portalContext'
import { useSessionKeeper } from '@hooks/useSessionKeeper'

// Lazy-load portals
const SuperAdminApp = lazy(() => import('@apps/super-admin/SuperAdminApp'))
const TenantApp = lazy(() => import('@apps/tenant-portal/TenantApp'))
const MunicipalityApp = lazy(() => import('@apps/municipality/MunicipalityApp'))
const PublicApp = lazy(() => import('@apps/public/PublicApp'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))

// ─── Loading Fallback ──────────────────────────────────────────────────────
function LoadingFallback() {
  // Same markup and styles as the splash in index.html, so the hand-over from the page to the app does not jump.
  return (
    <div className="boot" role="status" aria-live="polite">
      <div className="boot-route" aria-hidden="true">
        <span className="boot-stop boot-a"></span>
        <span className="boot-road"></span>
        <span className="boot-stop boot-b"></span>
        <img className="boot-logo" src="/kvbms-icon.svg" alt="" />
      </div>
      <div className="boot-name">CityBus</div>
    </div>
  )
}

// ─── Unauthorized Page ─────────────────────────────────────────────────────
function UnauthorizedPage() {
  const { t } = useTranslation('common')
  const { logout } = useAuthStore()
  const navigate = useNavigate()

  return (
    <ErrorPage
      code={403}
      fullScreen
      action={{
        label: t('errorPage.signInAgain'),
        onClick: () => {
          logout()
          navigate('/login', { replace: true })
        },
      }}
    />
  )
}

// Direct link to any error page (/error/502): handy for support and for checking the designs.
const ERROR_CODES = ['403', '404', '500', '502', '503', '504', 'offline'] as const
function ErrorRoute() {
  const { code = '' } = useParams()
  const known = (ERROR_CODES as readonly string[]).includes(code)
  return <ErrorPage code={known ? (code === 'offline' ? 'offline' : (Number(code) as 403)) : 404} fullScreen />
}

// ─── Portal Context Guard ──────────────────────────────────────────────────
// Ensures authenticated users are always on the domain that matches their role.
// An authenticated platform user on a tenant subdomain gets sent to the main
// domain, and an authenticated tenant user on the main domain gets sent to
// their company subdomain.
function PortalContextGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  const { isTenantPortal, isMunicipalityPortal } = getPortalContext()

  useEffect(() => {
    if (!isAuthenticated || !user) return

    if (isMunicipalityRole(user.role)) return // lives on its municipality host; ProtectedRoute scopes the paths
    if ((isTenantPortal || isMunicipalityPortal) && isPlatformRole(user.role)) {
      window.location.replace(getMainLoginUrl())
    } else if (!isTenantPortal && isTenantRole(user.role) && user.tenantSchema) {
      window.location.replace(
        getTenantLoginUrl(user.tenantSchema).replace('/login', landingPath(user.role))
      )
    }
  }, [isAuthenticated, user, isTenantPortal, isMunicipalityPortal])

  return <>{children}</>
}

// ─── Public vs Subdomain Root Guard ────────────────────────────────────────
// Tenant and municipality subdomains never show the public landing page or
// public rider pages; visitors are routed straight to sign-in (or their
// active portal home when already signed in).
function PublicOrSubdomainRoute() {
  const { isAuthenticated, user } = useAuthStore()
  const { isTenantPortal, isMunicipalityPortal } = getPortalContext()

  if (isTenantPortal || isMunicipalityPortal) {
    if (isAuthenticated && user) {
      if (isMunicipalityPortal && isMunicipalityRole(user.role)) {
        return <Navigate to={landingPath(user.role)} replace />
      }
      if (isTenantPortal && isTenantRole(user.role)) {
        return <Navigate to={landingPath(user.role)} replace />
      }
    }
    return <Navigate to="/login" replace />
  }

  return <PublicApp />
}

// ─── Protected Route ───────────────────────────────────────────────────────
function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode
  allowedRoles?: string[]
}) {
  const { isAuthenticated, user } = useAuthStore()

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Role mismatch — likely stale localStorage (e.g. PASSENGER from old session).
    // Send back to /login; LoginPage will detect and clear the invalid state on mount.
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  useGlobalNepaliKeyboard()
  useSessionKeeper()

  return (
    <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
    <PortalContextGuard>
      <Routes>
        {/* Public portal on main domain; redirects to /login (or portal home) on subdomains */}
        <Route path="/*" element={<PublicOrSubdomainRoute />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Super Admin portal */}
        <Route
          path="/super-admin/*"
          element={
            <ProtectedRoute
              allowedRoles={[
                'SUPER_ADMIN', 'TRANSPORT_AUTHORITY_OFFICER', 'REVENUE_AUDITOR',
                'COMPLIANCE_OFFICER', 'PLATFORM_SUPPORT',
              ]}
            >
              <SuperAdminApp />
            </ProtectedRoute>
          }
        />

        {/* Tenant (operator) ERP portal */}
        <Route
          path="/tenant/*"
          element={
            <ProtectedRoute
              allowedRoles={[
                'COMPANY_ADMIN', 'OPERATIONS_MANAGER', 'DISPATCHER',
                'FLEET_MANAGER', 'MAINTENANCE_MANAGER', 'STATION_MANAGER',
                'DRIVER', 'CONDUCTOR', 'INSPECTOR', 'OWNER',
                'FINANCE_OFFICER', 'HR_OFFICER', 'COMPANY_STAFF',
              ]}
            >
              <TenantApp />
            </ProtectedRoute>
          }
        />

        {/* Municipality (read-only oversight) portal */}
        <Route
          path="/municipality/*"
          element={
            <ProtectedRoute allowedRoles={['MUNICIPALITY_ADMIN']}>
              <MunicipalityApp />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/error/:code" element={<ErrorRoute />} />
      </Routes>
    </PortalContextGuard>
    </Suspense>
    </ErrorBoundary>
  )
}
